<template>
  <div id="meetList">
    <!-- 项目头部信息 -->
    <div class="project-header">
      <div class="project-info">
        <div class="project-name-row">
          <h2 class="project-name">{{ projectInfo.projectName || '项目详情' }}</h2>
          <el-tag size="small" :type="projectInfo.projectStatus === 'UNDERWAY' ? 'success' : 'info'">
            {{ projectInfo.projectStatus === 'UNDERWAY' ? '进行中' : '已结束' }}
          </el-tag>
        </div>
        <div class="project-meta">
          <span v-if="projectInfo.projectStartDate">
            <i class="el-icon-date"></i>
            {{ $moment(projectInfo.projectStartDate).format('YYYY.MM.DD') }}
            ~
            {{ $moment(projectInfo.projectEndDate).format('YYYY.MM.DD') }}
          </span>
          <span v-if="projectInfo.id"><i class="el-icon-document"></i> 项目编号: {{ projectInfo.id }}</span>
        </div>
      </div>

      <!-- 快捷操作 -->
      <div class="quick-actions">
        <div class="action-card create" @click="creatorMeet()" v-if="projectInfo.projectType !== 'single'">
          <div class="action-icon"><i class="el-icon-plus"></i></div>
          <div class="action-text">
            <span class="action-label">创建会议</span>
            <span class="action-sub">{{ projectInfo.useNum || 0 }} / {{ projectInfo.myMeetingNum || 0 }} 场</span>
          </div>
        </div>
        <div class="action-card doctor" @click="goDoctorList()">
          <div class="action-icon"><i class="el-icon-user"></i></div>
          <div class="action-text">
            <span class="action-label">项目医生</span>
            <span class="action-sub">管理参会专家</span>
          </div>
        </div>
      </div>
    </div>

    <!-- 状态筛选 Tab -->
    <div class="filter-section">
      <div class="status-tabs">
        <div v-for="(item, i) in list" :key="i"
             class="tab-item"
             :class="{ active: curNow === item.class }"
             @click="tabsChange(item)">
          {{ item.name }}
          <span class="tab-count" v-if="item.num !== undefined">{{ item.num }}</span>
        </div>
      </div>
      <div class="search-bar">
        <el-input v-model="searthForm.name" placeholder="搜索会议名称..."
                  prefix-icon="el-icon-search" clearable size="small"
                  class="search-input" @keyup.enter.native="getMeetingList()">
        </el-input>
        <el-select v-model="searthForm.location" filterable clearable placeholder="选择地区" size="small"
                   class="region-select">
          <el-option v-for="item in locationList" :key="item.id" :label="item.location"
                     :value="item.location"></el-option>
        </el-select>
        <el-button type="primary" size="small" icon="el-icon-search" @click="getMeetingList()">搜索</el-button>
        <el-button size="small" @click="searthForm = {}, getMeetingList()">重置</el-button>
      </div>
    </div>

    <!-- 会议卡片列表 -->
    <div class="meeting-list" v-loading="loading">
      <div class="meeting-card" v-for="item in meetingList" :key="item.id"
           @click="toMeetingDetails(item)">
        <!-- 左侧标识条 -->
        <div class="card-indicator" :class="getStatusColor(item.meetingStatus)"></div>

        <div class="card-content">
          <!-- 卡片头部 -->
          <div class="card-header">
            <div class="card-title">
              <span class="meeting-type-badge" :class="item.meetingType">
                {{ getMeetingTypeLabel(item) }}
              </span>
              <h3>{{ item.meetingTitle }}</h3>
            </div>
            <span class="meeting-id">#{{ item.id }}</span>
          </div>

          <!-- 卡片信息 -->
          <div class="card-meta">
            <div class="meta-item" v-if="item.meetingStartTime">
              <i class="el-icon-time"></i>
              {{ $moment(item.meetingStartTime).format('YYYY-MM-DD HH:mm') }}
            </div>
            <div class="meta-item" v-if="item.location">
              <i class="el-icon-location-outline"></i>
              {{ item.location }}
            </div>
            <div class="meta-item" v-if="item.reimbursementNum">
              <i class="el-icon-tickets"></i>
              报销编号: {{ item.reimbursementNum }}
            </div>
            <div class="meta-item" v-if="item.parentId && item.parentId !== item.id">
              <i class="el-icon-connection"></i>
              源自会议: #{{ item.parentId }}
            </div>
          </div>

          <!-- 卡片底部操作 -->
          <div class="card-footer">
            <el-tag size="mini" :type="getStatusTagType(item.meetingStatus)" effect="light">
              {{ getStatusLabel(item.meetingStatus) }}
            </el-tag>
            <div class="card-actions" @click.stop>
              <el-button size="mini" type="primary"
                         @click.stop="toMeetingDetails(item)">
                <i class="el-icon-document"></i> 会议详情
              </el-button>
              <el-button size="mini" type="warning" plain
                         @click.stop="claimMeeting(item)"
                         v-if="item.meetingStatus === 'WAIT_CLAIM'">
                <i class="el-icon-check"></i> 认领
              </el-button>
              <el-button size="mini" type="primary" plain
                         @click.stop="enterTheMeeting(item)"
                         v-if="item.meetingStatus === 'WAIT' || item.meetingStatus === 'LIVING'">
                <i class="el-icon-video-camera-solid"></i> 进入会议
              </el-button>
              <el-button size="mini" type="warning" plain
                         @click.stop="uploadContent(item)"
                         v-if="item.meetingStatus === 'UPLOAD'">
                <i class="el-icon-upload2"></i> 上传证明
              </el-button>
              <el-button size="mini" type="danger" plain
                         @click.stop="uploadContent(item)"
                         v-if="['AI_REVIEW_ERROR','OFFICIAL_ERROR','ERROR'].includes(item.meetingStatus)">
                <i class="el-icon-refresh"></i> 重新上传
              </el-button>
              <el-button size="mini" type="success" plain
                         @click.stop="startSettlement(item)"
                         v-if="canStartSettlement(item)">
                <i class="el-icon-money"></i> 发起结算
              </el-button>
            </div>
          </div>
        </div>
      </div>

      <!-- 空状态 -->
      <div class="empty-state" v-if="!loading && !meetingList.length">
        <img :src="require('@/assets/imgs/EmptyState.png')" alt="">
        <p>暂无会议数据</p>
        <el-button type="primary" size="small" @click="creatorMeet()"
                   v-if="projectInfo.projectType !== 'single'">
          <i class="el-icon-plus"></i> 创建第一场会议
        </el-button>
      </div>
    </div>

    <!-- 分页 -->
    <el-pagination v-if="total > size" background layout="prev, pager, next"
                   :total="total" :page-size="size"
                   :current-page.sync="page" @current-change="getMyProject"
                   class="pagination"></el-pagination>

    <!-- 公告弹窗 -->
    <el-dialog custom-class="notice-dialog" v-if="NoticeShow" :visible.sync="NoticeShow"
               :title="`${projectInfo.projectName} 公告`" width="700px" center>
      <div class="notice-content" v-html="Notice.message"></div>
      <span slot="footer">
        <el-button type="primary" @click="checkNotice">我已阅读并知晓</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import {
  todayMeeting,
  getMeetingUrl,
  uploadMeetingNum,
  meetList,
  projectList,
  getProjectById,
  getLocationListass,
  acceptProjectNotice,
  getProjectNotice,
  assistantGetProjectMeetingNum,
} from '@/request/api.js';

export default {
  name: 'meetList',
  data() {
    return {
      loading: false,
      projectInfo: '',
      projectNmae: '',
      curNow: "WAIT",
      upNum: 0,
      page: 1,
      size: 10,
      recentTotal: 0,
      total: 0,
      locationList: [],
      meetingList: [],
      todayMeetList: [],
      list: [
        { name: '待开始', class: 'WAIT' },
        { name: '待上传', class: 'UPLOAD' },
        { name: '审核中', class: 'REVIEW' },
        { name: '被驳回', class: 'ERROR' },
        { name: '已结束', class: 'FINISH' },
      ],
      searthForm: { name: "", project: "" },
      projectList: [],
      Notice: '',
      NoticeShow: false,
    }
  },
  created() {
    // localStorage 中可能没有 projectDetails (从其他入口直接进来), 用 {} 兜底避免后续访问 null 属性报错
    try {
      this.projectInfo = JSON.parse(localStorage.getItem('projectDetails')) || {};
    } catch (e) {
      this.projectInfo = {};
    }
    this.getTodayMeeting();
    this.getUploadMeetingNum();
    this.getMeetingFirst()
    this.getMeetingList();
    this.getProjectList();
    this.getLocationListFun();
    this.getNotice()
    this.getProjectByIdFun();
    this.getTotals()
  },
  methods: {
    // ====== 状态辅助 ======
    getMeetingTypeLabel(item) {
      if (item.meetingType === 'single' && item.parentId) return '追加会'
      if (item.meetingType === 'single') return '单场会'
      return '系列会'
    },
    getStatusLabel(status) {
      const map = {
        WAIT: '待开始', WAIT_CLAIM: '待认领', LIVING: '进行中', FINISH: '已结束',
        UPLOAD: '待上传', AI_REVIEW: '预审中', AI_REVIEW_ERROR: '预审驳回',
        AI_REVIEW_ERROR_ACCEPT: '官员审核', AI_REVIEW_SUCCESS: '官员审核',
        OFFICIAL_ERROR: '官员驳回', OFFICIAL_SUCCESS: '财务审核',
        ERROR: '财务驳回', SUCCESS: '审核通过',
      }
      return map[status] || status
    },
    getStatusTagType(status) {
      if (['WAIT', 'WAIT_CLAIM'].includes(status)) return 'info'
      if (['LIVING'].includes(status)) return 'warning'
      if (['UPLOAD'].includes(status)) return ''
      if (['AI_REVIEW', 'AI_REVIEW_SUCCESS', 'AI_REVIEW_ERROR_ACCEPT', 'OFFICIAL_SUCCESS'].includes(status)) return 'success'
      if (['AI_REVIEW_ERROR', 'OFFICIAL_ERROR', 'ERROR'].includes(status)) return 'danger'
      if (['SUCCESS', 'FINISH'].includes(status)) return 'success'
      return 'info'
    },
    getStatusColor(status) {
      if (['LIVING'].includes(status)) return 'living'
      if (['AI_REVIEW_ERROR', 'OFFICIAL_ERROR', 'ERROR'].includes(status)) return 'error'
      if (['SUCCESS', 'FINISH'].includes(status)) return 'success'
      if (['AI_REVIEW', 'AI_REVIEW_SUCCESS', 'AI_REVIEW_ERROR_ACCEPT', 'OFFICIAL_SUCCESS'].includes(status)) return 'review'
      if (['UPLOAD'].includes(status)) return 'upload'
      return 'default'
    },

    // ====== 数据加载 ======
    getProjectByIdFun() {
      getProjectById({ id: this.$route.query.pId }).then(res => {
        if (res.code == 200) this.projectInfo = res.data
      })
    },
    checkNotice() {
      acceptProjectNotice({
        noticeId: this.Notice.id,
        projectId: this.projectInfo.id
      }).then(res => {
        if (res.code == 200) this.NoticeShow = false
      })
    },
    getNotice() {
      getProjectNotice({ projectId: this.projectInfo.id }).then(res => {
        if (res.code == 200) {
          this.Notice = res.data
          if (!this.Notice.isCheck) this.NoticeShow = true
        }
      })
    },
    getLocationListFun() {
      getLocationListass().then(res => {
        if (res.code == 200) this.locationList = res.data
      })
    },
    getProjectList(query) {
      projectList({ current: 1, size: 30, projectName: query }).then(res => {
        if (res.code == 200) this.projectList = res.data.records
      })
    },

    // ====== 导航 ======
    // ====== G11: 一键发起结算 ======
    canStartSettlement(item) {
      // 会议已结束(任意"上传/审核中/已完结"阶段)且尚无结算单
      const endedStatuses = ['UPLOAD', 'AI_REVIEW', 'AI_REVIEW_SUCCESS', 'AI_REVIEW_ERROR_ACCEPT',
        'OFFICIAL_SUCCESS', 'OFFICIAL_ERROR', 'FINISH']
      return endedStatuses.includes(item.meetingStatus) && !item.hasSpendingOrder
    },
    startSettlement(item) {
      // 跳转结算单管理页面,带上会议ID,由 SpendingManage 自动打开新建对话框并预填
      this.$router.push({
        path: '/spendingManage',
        query: { fromMeetingId: item.id }
      })
    },

    claimMeeting(item) {
      this.$router.push({ name: 'createmeet', query: { pId: this.$route.query.pId, mId: item.id } })
    },
    toMeetingDetails(item) {
      this.$router.push({ name: 'meetdetails', query: { mId: item.id, status: item.meetingStatus, now: '3' } })
    },
    uploadContent(item) {
      this.$router.push({ name: 'uploadFile', query: { mId: item.id } })
    },
    goDoctorList() {
      this.$router.push({ name: 'doctor' })
    },
    creatorMeet() {
      if (this.projectInfo.myMeetingNum > this.projectInfo.useNum) {
        this.$router.push({ name: 'createmeet', query: { pId: this.$route.query.pId } })
      } else {
        this.$alert('您在本项目中的会议配额已达上限，<br/>如需新建会议请联系项目官员。', '配额超限', {
          dangerouslyUseHTMLString: true,
          confirmButtonText: '确定',
          type: "warning",
          center: true
        })
      }
    },

    // ====== 数据请求 ======
    getTotals() {
      assistantGetProjectMeetingNum({ projectId: this.$route.query.pId }).then(res => {
        if (res.code == 200) {
          this.list.forEach(item => {
            if (item.class === 'WAIT') item.num = res.data.waitMeetingNum
            else if (item.class === 'UPLOAD') item.num = res.data.uploadMeetingNum
            else if (item.class === 'REVIEW') item.num = res.data.reviewMeetingNum
            else if (item.class === 'ERROR') item.num = res.data.errorMeetingNum
            else if (item.class === 'FINISH') item.num = res.data.successMeetingNum
          })
          this.$forceUpdate()
        }
      })
    },
    getMeetingList() {
      let data = {
        projectId: this.$route.query.pId,
        size: this.size,
        current: this.page,
        meetingType: this.curNow,
      }
      if (this.searthForm.name) data.meetingTitle = this.searthForm.name
      else if (this.searthForm.location) data.location = this.searthForm.location

      meetList(data).then(res => {
        if (res.code == 200) {
          let resData = res.data.records
          resData.map(item => {
            if (item.id == item.parentId) delete item.parentId
            return item
          })
          this.meetingList = resData
          this.total = res.data.total
          if (this.meetingList[0]) this.projectNmae = this.meetingList[0].projectName
        }
      })
    },
    getMeetingFirst() {
      meetList({ projectId: this.$route.query.pId, meetingType: "WAIT" }).then(res => {
        if (res.code == 200) this.recentTotal = res.data.total
      })
    },
    getUploadMeetingNum() {
      uploadMeetingNum({ projectId: this.$route.query.pId }).then(res => {
        if (res.code == 200) this.upNum = res.data
      })
    },
    enterTheMeeting(item) {
      getMeetingUrl({ meetingId: item.id }).then(res => {
        if (res.code == 200) {
          window.open(res.data)
        } else {
          this.$message({ message: res.msg, type: 'error' })
        }
      })
    },
    getTodayMeeting() {
      todayMeeting({}).then(res => {
        if (res.code == 200) {
          let data = res.data
          data.map(item => { item.doctors = item.doctors.split(',').join("，"); return item })
          this.todayMeetList = data
        }
      })
    },
    getMyProject(e) { this.page = e; this.getMeetingList() },
    tabsChange(item) { this.curNow = item.class; this.page = 1; this.getMeetingList(); this.getTotals() },
  }
}
</script>

<style lang="scss" scoped>
#meetList {
  padding: 20px;
  min-height: 100vh;
  background: #f5f7fa;
}

/* ===== 项目头部 ===== */
.project-header {
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
  border-radius: 16px;
  padding: 28px 32px;
  margin-bottom: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 20px;
  color: #fff;

  .project-info {
    flex: 1;
    min-width: 300px;

    .project-name-row {
      display: flex;
      align-items: center;
      gap: 12px;
      margin-bottom: 10px;

      .project-name {
        font-size: 22px;
        font-weight: 700;
        margin: 0;
        letter-spacing: 0.5px;
      }
    }

    .project-meta {
      display: flex;
      gap: 24px;
      font-size: 13px;
      color: rgba(255, 255, 255, 0.7);

      span {
        display: flex;
        align-items: center;
        gap: 5px;
      }
    }
  }

  .quick-actions {
    display: flex;
    gap: 12px;

    .action-card {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 14px 20px;
      border-radius: 12px;
      cursor: pointer;
      transition: all 0.3s ease;
      min-width: 160px;

      &:hover { transform: translateY(-2px); box-shadow: 0 6px 20px rgba(0,0,0,0.2); }

      &.create {
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      }
      &.doctor {
        background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
      }

      .action-icon {
        width: 40px;
        height: 40px;
        border-radius: 10px;
        background: rgba(255,255,255,0.2);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 18px;
      }

      .action-text {
        display: flex;
        flex-direction: column;

        .action-label {
          font-size: 15px;
          font-weight: 600;
        }
        .action-sub {
          font-size: 12px;
          color: rgba(255,255,255,0.8);
          margin-top: 2px;
        }
      }
    }
  }
}

/* ===== 筛选区域 ===== */
.filter-section {
  background: #fff;
  border-radius: 12px;
  padding: 16px 20px;
  margin-bottom: 16px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.06);

  .status-tabs {
    display: flex;
    gap: 4px;
    margin-bottom: 14px;
    border-bottom: 1px solid #ebeef5;
    padding-bottom: 12px;

    .tab-item {
      padding: 8px 18px;
      font-size: 14px;
      color: #606266;
      cursor: pointer;
      border-radius: 8px;
      transition: all 0.25s;
      display: flex;
      align-items: center;
      gap: 6px;

      &:hover { background: #f0f2f5; }
      &.active {
        background: #409eff;
        color: #fff;
        font-weight: 600;
      }

      .tab-count {
        background: rgba(0,0,0,0.08);
        padding: 1px 7px;
        border-radius: 10px;
        font-size: 12px;
        min-width: 20px;
        text-align: center;
      }
      &.active .tab-count { background: rgba(255,255,255,0.25); }
    }
  }

  .search-bar {
    display: flex;
    gap: 10px;
    align-items: center;

    .search-input { width: 280px; }
    .region-select { width: 160px; }
  }
}

/* ===== 会议卡片 ===== */
.meeting-list {
  min-height: 200px;
}

.meeting-card {
  background: #fff;
  border-radius: 12px;
  margin-bottom: 12px;
  display: flex;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.25s;
  box-shadow: 0 1px 4px rgba(0,0,0,0.06);

  &:hover {
    box-shadow: 0 4px 16px rgba(0,0,0,0.1);
    transform: translateY(-1px);
  }

  .card-indicator {
    width: 4px;
    flex-shrink: 0;

    &.default { background: #909399; }
    &.living { background: #e6a23c; animation: pulse-glow 2s infinite; }
    &.error { background: #f56c6c; }
    &.success { background: #67c23a; }
    &.review { background: #409eff; }
    &.upload { background: #e6a23c; }
  }

  .card-content {
    flex: 1;
    padding: 18px 20px;

    .card-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin-bottom: 10px;

      .card-title {
        display: flex;
        align-items: center;
        gap: 8px;

        .meeting-type-badge {
          font-size: 11px;
          padding: 2px 8px;
          border-radius: 4px;
          color: #fff;
          white-space: nowrap;

          &.single { background: #909399; }
          &.series { background: #409eff; }
        }

        h3 {
          margin: 0;
          font-size: 16px;
          font-weight: 600;
          color: #303133;
        }
      }

      .meeting-id {
        font-size: 12px;
        color: #c0c4cc;
        white-space: nowrap;
      }
    }

    .card-meta {
      display: flex;
      gap: 20px;
      margin-bottom: 12px;
      flex-wrap: wrap;

      .meta-item {
        font-size: 13px;
        color: #909399;
        display: flex;
        align-items: center;
        gap: 4px;

        i { font-size: 14px; }
      }
    }

    .card-footer {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding-top: 10px;
      border-top: 1px solid #f2f6fc;

      .card-actions {
        display: flex;
        gap: 6px;
      }
    }
  }
}

/* ===== 空状态 ===== */
.empty-state {
  text-align: center;
  padding: 80px 20px;
  background: #fff;
  border-radius: 12px;

  img { width: 160px; opacity: 0.6; }
  p { color: #909399; font-size: 15px; margin: 16px 0 20px; }
}

/* ===== 分页 ===== */
.pagination {
  display: flex;
  justify-content: center;
  padding: 20px 0;
}

/* ===== 动画 ===== */
@keyframes pulse-glow {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

/* ===== 公告弹窗 ===== */
.notice-content {
  max-height: 400px;
  overflow-y: auto;
  line-height: 1.8;
  font-size: 14px;
  color: #333;
}
</style>